import { useEffect, useRef } from "react";

/**
 * Animated iPhone iMessage mockup. Drops in as a React component.
 * All styles are scoped under the `.isd-iphone` namespace to avoid leaks.
 * Conversation loops indefinitely; respects prefers-reduced-motion.
 */
export default function IPhoneMockup() {
  const messagesRef = useRef(null);
  const typingRef = useRef(null);
  const waveRef = useRef(null);

  useEffect(() => {
    // Build the audio waveform bars
    const waveHeights = [
      3, 5, 8, 12, 16, 22, 18, 26, 30, 34, 28, 36, 32, 36, 30, 24, 32, 26, 20,
      28, 22, 16, 12, 8, 5, 4, 7, 10, 14, 20, 16, 24,
    ];
    const wave = waveRef.current;
    const messages = messagesRef.current;
    const typing = typingRef.current;
    if (!wave || !messages || !typing) return;

    wave.innerHTML = "";
    waveHeights.forEach((height, index) => {
      const bar = document.createElement("span");
      bar.style.height = `${height}px`;
      bar.style.animationDelay = `${index * 40}ms`;
      bar.style.background =
        index < 19 ? "var(--isd-blue-soft)" : "rgba(91,130,250,0.35)";
      wave.appendChild(bar);
    });

    const conversation = [
      { from: "out", text: "Hey Greg, did you receive that contract?" },
      { from: "in", text: "Yeah, thanks Sara!" },
      { from: "out", text: "Perfect. Sending over the proposal now." },
      { from: "in", text: "This is exactly what we needed." },
      { from: "out", text: "Ready to get started this week?" },
      { from: "in", text: "Absolutely. Let's do it!" },
    ];

    let cancelled = false;
    const wait = (ms) =>
      new Promise((resolve) => {
        const t = setTimeout(resolve, ms);
        return () => clearTimeout(t);
      });

    const addBubble = (message) => {
      const bubble = document.createElement("div");
      bubble.className = `bubble ${message.from}`;
      bubble.textContent = message.text;
      messages.insertBefore(bubble, typing);
    };
    const clearDynamicBubbles = () => {
      messages
        .querySelectorAll(".bubble.out, .bubble.in:not(.voice)")
        .forEach((b) => b.remove());
    };

    async function loop() {
      while (!cancelled) {
        clearDynamicBubbles();
        await wait(700);
        if (cancelled) return;
        for (const message of conversation) {
          if (cancelled) return;
          typing.classList.add("show");
          await wait(message.from === "in" ? 900 : 520);
          typing.classList.remove("show");
          addBubble(message);
          await wait(1150);
        }
        await wait(1800);
      }
    }
    loop();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="isd-iphone" aria-label="Animated iPhone message mockup">
      <style>{CSS}</style>
      <div className="phone-wrap">
        <section className="phone">
          <span className="button left short" />
          <span className="button left mid-one" />
          <span className="button left mid-two" />
          <span className="button right" />

          <div className="screen">
            <div className="island" />

            <div className="status">
              <span>10:59</span>
              <div className="status-icons" aria-hidden="true">
                <div className="bars">
                  <span /><span /><span /><span />
                </div>
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                  <path d="M8 9.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" fill="white" />
                  <path d="M2.5 6.2a8 8 0 0 1 11 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M.5 3.5a11 11 0 0 1 15 0" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity=".4" />
                </svg>
                <div className="battery">
                  <div className="battery-body">
                    <div className="battery-fill" />
                  </div>
                  <div className="battery-cap" />
                </div>
              </div>
            </div>

            <div className="chat-header">
              <svg className="back" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                <path d="M7 1 1 7l6 6" stroke="#5B82FA" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <img
                className="avatar"
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80&auto=format&fit=crop"
                alt=""
              />
              <div>
                <div className="name">Greg Smith</div>
                <div className="lead">New Lead</div>
              </div>
            </div>

            <div className="date">Today 2:34 PM</div>

            <div className="messages" ref={messagesRef}>
              <div className="voice bubble in" style={{ animationDelay: "80ms" }}>
                <div className="play" aria-hidden="true">
                  <svg width="10" height="12" viewBox="0 0 10 12" fill="white">
                    <path d="M2 1 9 6l-7 5V1Z" />
                  </svg>
                </div>
                <div className="wave" ref={waveRef} aria-hidden="true" />
                <div className="voice-time">0:04</div>
                <div className="quote">"You absolutely rock."</div>
              </div>

              <div className="photo">
                <img
                  src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=480&q=80&auto=format&fit=crop"
                  alt=""
                />
                <div className="photo-play" aria-hidden="true">
                  <svg width="14" height="16" viewBox="0 0 14 16" fill="white">
                    <path d="M2 1 13 8 2 15V1Z" />
                  </svg>
                </div>
              </div>

              <div className="typing" ref={typingRef}>
                <span /><span /><span />
              </div>
            </div>

            <div className="composer">
              <div className="round" aria-hidden="true">+</div>
              <div className="input">
                iMessage <span aria-hidden="true">⌕</span>
              </div>
              <div className="round send" aria-hidden="true">
                <svg width="11" height="11" viewBox="0 0 11 11" fill="white">
                  <path d="M1 10 10 5.5 1 1v3.5l6 1-6 1V10Z" />
                </svg>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* Scoped CSS — every selector lives under `.isd-iphone` so it cannot
   pollute the rest of the site. */
const CSS = `
.isd-iphone {
  --isd-blue: #1982fc;
  --isd-blue-soft: #5b82fa;
  --isd-text: #f7f8ff;
  --isd-bubble-in: rgba(255,255,255,0.12);
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  isolation: isolate;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
}
.isd-iphone *, .isd-iphone *::before, .isd-iphone *::after { box-sizing: border-box; }
.isd-iphone::before {
  content: "";
  position: absolute;
  width: min(70%, 520px);
  aspect-ratio: 1;
  border-radius: 999px;
  background: radial-gradient(circle, rgba(91,130,250,0.18), transparent 66%);
  filter: blur(26px);
  z-index: -1;
}
.isd-iphone .phone-wrap {
  perspective: 1200px;
  animation: isdPhoneEnter 900ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.isd-iphone .phone {
  width: min(72vw, 296px);
  aspect-ratio: 296 / 604;
  position: relative;
  padding: 6px;
  border-radius: 46px;
  background: linear-gradient(150deg, #454548 0%, #16171b 42%, #303035 100%);
  border: 1.5px solid rgba(255,255,255,0.18);
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.9),
    0 34px 70px rgba(0,0,0,0.72),
    inset 0 1px 0 rgba(255,255,255,0.16);
  transform: rotateY(-8deg) rotateX(3deg);
}
.isd-iphone .button { position: absolute; width: 2px; border-radius: 2px; background: #4b4b50; }
.isd-iphone .button.left.short { left: -2px; top: 80px; height: 16px; }
.isd-iphone .button.left.mid-one { left: -2px; top: 108px; height: 26px; }
.isd-iphone .button.left.mid-two { left: -2px; top: 144px; height: 26px; }
.isd-iphone .button.right { right: -2px; top: 120px; height: 48px; }
.isd-iphone .screen {
  width: 100%; height: 100%;
  position: relative; overflow: hidden;
  border-radius: 41px; background: #000;
}
.isd-iphone .island {
  position: absolute; top: 12px; left: 50%;
  width: 114px; height: 32px;
  border-radius: 999px; transform: translateX(-50%);
  background: #000; z-index: 4;
}
.isd-iphone .status {
  height: 52px; display: flex; align-items: end; justify-content: space-between;
  padding: 0 20px 6px; color: white; font-size: 15px; font-weight: 650;
}
.isd-iphone .status-icons { display: flex; align-items: center; gap: 7px; }
.isd-iphone .bars { display: flex; align-items: end; gap: 2px; width: 17px; height: 12px; }
.isd-iphone .bars span { width: 3px; border-radius: 1px; background: white; }
.isd-iphone .bars span:nth-child(1) { height: 4px; }
.isd-iphone .bars span:nth-child(2) { height: 7px; }
.isd-iphone .bars span:nth-child(3) { height: 10px; }
.isd-iphone .bars span:nth-child(4) { height: 12px; opacity: 0.35; }
.isd-iphone .battery { width: 26px; height: 12px; display: flex; align-items: center; gap: 2px; }
.isd-iphone .battery-body { width: 22px; height: 11px; padding: 1px; border: 1px solid rgba(255,255,255,0.38); border-radius: 3px; }
.isd-iphone .battery-fill { width: 74%; height: 100%; border-radius: 2px; background: #4cd964; }
.isd-iphone .battery-cap { width: 2px; height: 5px; border-radius: 0 1px 1px 0; background: rgba(255,255,255,0.45); }
.isd-iphone .chat-header { display: flex; align-items: center; gap: 10px; padding: 8px 14px 10px; border-bottom: 0.5px solid rgba(255,255,255,0.08); }
.isd-iphone .back { width: 8px; height: 14px; }
.isd-iphone .avatar { width: 30px; height: 30px; border-radius: 50%; object-fit: cover; }
.isd-iphone .name { color: var(--isd-text); font-size: 13px; font-weight: 700; line-height: 1.2; }
.isd-iphone .lead { margin-top: 1px; color: rgba(255,255,255,0.42); font-size: 10px; }
.isd-iphone .date { margin: 7px 0 6px; text-align: center; color: rgba(255,255,255,0.35); font-size: 11px; }
.isd-iphone .messages {
  height: calc(100% - 52px - 49px - 25px - 55px);
  padding: 0 10px 8px; display: flex; flex-direction: column; gap: 6px; overflow: hidden;
}
.isd-iphone .bubble {
  max-width: 80%; padding: 8px 12px; border-radius: 18px;
  color: #fff; font-size: 13px; line-height: 1.36;
  opacity: 0; transform: translateY(10px) scale(0.96);
  animation: isdBubbleIn 420ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.isd-iphone .bubble.out { align-self: flex-end; background: var(--isd-blue); border-bottom-right-radius: 5px; }
.isd-iphone .bubble.in { align-self: flex-start; background: var(--isd-bubble-in); border-bottom-left-radius: 5px; }
.isd-iphone .voice {
  width: 212px; max-width: 88%;
  display: grid; grid-template-columns: 28px 1fr 30px; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 20px; background: var(--isd-bubble-in);
}
.isd-iphone .play { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: var(--isd-blue); }
.isd-iphone .wave { height: 30px; display: flex; align-items: center; gap: 2px; }
.isd-iphone .wave span { width: 2.5px; border-radius: 2px; background: var(--isd-blue-soft); animation: isdWave 1.2s ease-in-out infinite; }
.isd-iphone .voice-time { color: rgba(255,255,255,0.62); font-size: 11px; }
.isd-iphone .quote { grid-column: 2 / span 1; margin-top: -2px; color: rgba(255,255,255,0.84); font-size: 10px; font-style: italic; }
.isd-iphone .photo { width: 180px; height: 120px; position: relative; align-self: flex-end; overflow: hidden; border-radius: 15px; background: #20222c; opacity: 0; transform: translateY(10px) scale(0.97); animation: isdBubbleIn 420ms 220ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.isd-iphone .photo img { width: 100%; height: 100%; object-fit: cover; }
.isd-iphone .photo::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent, rgba(0,0,0,0.16)); }
.isd-iphone .photo-play { position: absolute; top: 50%; left: 50%; width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; background: rgba(0,0,0,0.58); backdrop-filter: blur(5px); transform: translate(-50%, -50%); z-index: 2; }
.isd-iphone .typing { align-self: flex-start; display: none; gap: 4px; align-items: center; padding: 10px 14px; border-radius: 18px 18px 18px 5px; background: var(--isd-bubble-in); }
.isd-iphone .typing.show { display: flex; animation: isdBubbleIn 260ms cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.isd-iphone .typing span { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,0.62); animation: isdTyping 520ms ease-in-out infinite; }
.isd-iphone .typing span:nth-child(2) { animation-delay: 150ms; }
.isd-iphone .typing span:nth-child(3) { animation-delay: 300ms; }
.isd-iphone .composer { position: absolute; left: 0; right: 0; bottom: 0; display: flex; align-items: center; gap: 6px; padding: 8px 10px 12px; border-top: 0.5px solid rgba(255,255,255,0.08); background: rgba(0,0,0,0.82); }
.isd-iphone .round { width: 26px; height: 26px; display: grid; place-items: center; flex: 0 0 auto; border-radius: 50%; background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.55); font-size: 17px; }
.isd-iphone .input { min-width: 0; flex: 1; display: flex; align-items: center; justify-content: space-between; height: 30px; padding: 0 10px; border-radius: 16px; background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.33); font-size: 12px; }
.isd-iphone .send { background: var(--isd-blue-soft); }

@keyframes isdPhoneEnter {
  from { opacity: 0; transform: translateY(60px) scale(0.92); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes isdBubbleIn { to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes isdTyping { 50% { transform: translateY(-5px); } }
@keyframes isdWave {
  0%,100% { transform: scaleY(1); opacity: .95; }
  35%      { transform: scaleY(.45); opacity: .55; }
  70%      { transform: scaleY(1.25); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .isd-iphone *, .isd-iphone *::before, .isd-iphone *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
  }
}
`;
